import { createContext, useContext } from 'react';
import type { ChatDocumentDTO } from '@shared/schemas/chat';
import type { ChunkAnchor } from '@shared/schemas/rag-anchors';

export type ActiveSourceReference = {
  anchor?: ChunkAnchor | null;
  blockId?: string;
  chunkRerankScore?: number;
  endLine?: number;
  excerpt?: string;
  isVerifiable?: boolean;
  page: number;
  referenceId: string;
  sectionPath?: string[];
  source?: 'hybrid' | 'pageindex_tool';
  startLine?: number;
};

export type ChatExperienceContextValue = {
  activeDocument: ChatDocumentDTO | null;
  activeSourceReference: ActiveSourceReference | null;
  activeSourceReferences: ActiveSourceReference[];
  clearDocument: () => void;
  closeDocument: () => void;
  isDocumentOpen: boolean;
  openDocument: (
    document: ChatDocumentDTO,
    reference?: ActiveSourceReference | null,
    references?: ActiveSourceReference[] | null,
  ) => void;
  patchDocument: (
    documentId: string,
    patch: Partial<ChatDocumentDTO> | ((current: ChatDocumentDTO) => ChatDocumentDTO),
  ) => void;
  setActiveSourceReference: (reference: ActiveSourceReference | null) => void;
};

export const ChatExperienceContext = createContext<ChatExperienceContextValue | null>(null);

export function useChatExperience() {
  const context = useContext(ChatExperienceContext);

  if (!context) {
    throw new Error('useChatExperience must be used within ChatExperienceProvider.');
  }

  return context;
}
